<template>
  <v-card outlined width="100%">
    <div class="align-center">
      <div
        style="display: grid"
        class="py-3 subtitle-1 justify-center align-center"
      >
        <span class="title"> {{ $t("statistics.lblTimerRecordedData") }} </span>
      </div>
      <div>
        <a-data-table
          :headers="headers"
          :items.sync="paginatedResult"
          :page="page"
          :items-per-page="paginatedResult.length"
          dense
          class="elevation-1 timer-table"
          :no-data-text="$t('noData')"
          :no-results-text="$t('emptySearchRecords')"
          sort-by="meetingName"
          hide-default-footer
          fixed-header
        >
          <template v-slot:item="{ item }">
            <tr>
              <td
                v-if="!item.isRowSpan"
                :rowspan="getRowspan(item)"
                class="top-align border-right" style="word-break: break-word;"
              >
               {{ item.meetingName }}
              </td>
              <td><span class="text-wrap">{{ item.topicName }}</span></td>
              <td>{{ item.plannedStartTime }}</td>
              <td>{{ item.plannedEndTime }}</td>
              <td class="right-align">{{ item.plannedTime }}</td>
              <td>{{ item.startMeetingTime }}</td>
              <td>{{ item.endMeetingTime }}</td>
              <td class="right-align">
                {{ item.meetingActualTimeMin }}
              </td>
            </tr>
          </template>

          <!-- Custom Pagination -->
          <template v-slot:footer>
            <div class="v-data-footer" style="margin: 0;">
              <!-- Items per page selector -->
              <div class="v-data-footer__select">
                {{ $t("statistics.lblMeetingsPerPage") }}: 
                <v-select
                  v-model="itemsPerPage"
                  :items="[5, 10, 25, 50]"
                  style="max-width: 60px; margin: 20px 0 6px 34px;"
                  dense
                  @change="changeItemsPerPage"
                />
              </div>

              <!-- Displaying item count -->
              <div class="v-data-footer__pagination">
                <span>{{ startItem }} - {{ endItem }} of {{ meetingNames.length }}</span>
              </div>
              
              <!-- Custom Pagination Controls -->
              <div class="d-flex justify-center">
                <v-btn
                  :disabled="page === 1"
                  @click="prevPage"
                  icon
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn
                  :disabled="page === totalPages"
                  @click="nextPage"
                  icon
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>
          </template>
        </a-data-table>
      </div>
    </div>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
export default {
  name: "TimerRecordedData",
  props: {
    filteredMeetingList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      meetingNames: [],
      paginatedResult: [],
      pageCount: 0,
      itemsPerPage: 5,
      page: 1,
      options: {
        page: 1,
        itemsPerPage: 5,
        sortBy: [],
        sortDesc: [],
      },
    };
  },
  computed: {
    ...mapState({
      meetingsList: (state) => state.meeting.customMeetingsList,
    }),
    headers() {
      return [
        {
          text: this.$t("agenda.lblMeetingTitle"),
          align: "start",
          value: "meetingName",
          class: "header header-text",
        },
        {
          text: this.$t("statistics.lblTopic"),
          value: "topicName",
          class: "header header-text",
          sortable: false,
        },
        {
          text: this.$t("statistics.lblPlannedStartTime"),
          value: "plannedStartTime",
          class: "header header-text",
          sortable: false,
        },
        {
          text: this.$t("statistics.lblPlannedEndTime"),
          value: "plannedEndTime",
          class: "header header-text",
          sortable: false,
        },
        {
          text: this.$t("statistics.lblPlannedTimemMin"),
          value: "plannedTime",
          align: "end",
          class: "header header-text",
          sortable: false,
        },
        {
          text: this.$t("statistics.lblActualStartTime"),
          value: "startMeetingTime",
          class: "header header-text",
          sortable: false,
        },
        {
          text: this.$t("statistics.lblActualEndTime"),
          value: "endMeetingTime",
          class: "header header-text",
          sortable: false,
        },
        {
          text: this.$t("statistics.lblActualTime"),
          value: "meetingActualTimeMin",
          class: "header header-text",
          align: "end",
          sortable: false,
        },
      ];
    },
    sortedMeetingList() {
      return this.filteredMeetingList.sort(
        (a, b) =>
          new Date(a.scheduletStartTimeInUTC) -
          new Date(b.scheduletStartTimeInUTC)
      );
    },
    getMeetingTimerData() {
      return this.sortedMeetingList.reduce((timerDetails, meeting) => {
        // Add meeting details
        if (!timerDetails[meeting.meetingName]) {
          timerDetails[meeting.meetingName] = [];
          timerDetails[meeting.meetingName].push({
            id: meeting.id,
            meetingName: meeting.meetingName,
            topicName: "-",
            plannedStartTime: this.getConvertedDateTime(
              meeting.scheduletStartTimeInUTC
            ),
            plannedEndTime: this.getConvertedDateTime(
              meeting.scheduledEndTimeInUTC
            ),
            plannedTime: this.actualTotalTimeInSecond(
              meeting.scheduletStartTimeInUTC,
              meeting.scheduledEndTimeInUTC
            ),
            startMeetingTime: meeting.startMeetingTime
              ? this.getConvertedDateTime(meeting.startMeetingTime)
              : "",
            endMeetingTime: meeting.endMeetingTime
              ? this.getConvertedDateTime(meeting.endMeetingTime)
              : "",
            meetingActualTimeMin: meeting.meeting_actual_time_min || "",
            isRowSpan: false,
          });
        }
        // Add topic details for each meeting
        meeting.topics.forEach((topic) => {
          timerDetails[meeting.meetingName].push({
            id: topic.Id,
            meetingName: meeting.meetingName,
            topicName: topic.topicName,
            plannedStartTime: "-",
            plannedEndTime: "-",
            plannedTime: topic.timeInMinutes,
            startMeetingTime: topic.starttime
              ? this.getConvertedDateTime(topic.starttime)
              : "",
            endMeetingTime: topic.endtime
              ? this.getConvertedDateTime(topic.endtime)
              : "",
            meetingActualTimeMin: topic.topic_actual_time_min || "",
            isRowSpan: true,
          });
        });
        return timerDetails;
      }, []);
    },
    totalItems() {
      return this.getMeetingTimerData.length;
    },
    meetingItems() {
      return this.getMeetingTimerData;
    },
    totalPages() {
      return Math.ceil(this.meetingNames.length / this.itemsPerPage);
    },
    startItem() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    endItem() {
      return Math.min(this.page * this.itemsPerPage, this.meetingNames.length);
    },
  },
  mounted() {
    this.paginatation(this.meetingItems, this.itemsPerPage, this.page)
  },
  methods: {
    getConvertedDateTime,
    actualTotalTimeInSecond,
    prevPage() {
      this.page = this.page - 1.
      this.paginatation(this.meetingItems, this.itemsPerPage, this.page)
    },
    nextPage() {
      this.page = this.page + 1;
      this.paginatation(this.meetingItems, this.itemsPerPage, this.page)
    },
    changeItemsPerPage(updatedItemsPerPage) {
      this.page = 1;
      this.paginatation(this.meetingItems, updatedItemsPerPage > this.meetingNames.length ?  this.meetingNames.length : updatedItemsPerPage, this.page)
    },
    paginatation(groupedData, pageSize, currentPage) {
      this.meetingNames = Object.keys(groupedData);

      // Calculate start and end indexes for the current page
      const startIdx = (currentPage - 1) * pageSize;
      const endIdx = startIdx + pageSize;
      
      // Get the current page subset of meeting names
      const pageMeetingNames = this.meetingNames.slice(startIdx, endIdx);
      
      // Prepare paginated result
      let resultArr = pageMeetingNames.reduce((result, meetingName) => {
        result[meetingName] = groupedData[meetingName];
        return result;
      }, {});
      this.paginatedResult = Object.values(resultArr).flat();
    },
    getRowspan(item) {
      return this.paginatedResult.filter((i) => i.meetingName === item.meetingName)
        .length;
    },
  },
};

function actualTotalTimeInSecond(sTime, eTime) {
  const startTime = new Date(sTime);
  const endTime = new Date(eTime);

  const timeDiffInMilliseconds = endTime - startTime;
  const timeDiffInMinutes = Math.round(timeDiffInMilliseconds / (1000 * 60));

  return timeDiffInMinutes;
}
function getConvertedDateTime(scheduleTime) {
  const date = new Date(scheduleTime);
  if (date && isValidDate(date)) {
    // const date = new Date(scheduletTime);
    const time = new Intl.DateTimeFormat(this.selectedLang, {
      timeStyle: "short",
    }).format(date);

    const d = date.getDate();
    const day = d < 10 ? `0${d}` : d;
    const m = date.getMonth() + 1; //Month from 0 to 11
    const month = m < 10 ? `0${m}` : m;
    const y = date.getFullYear();
    return `${y}/${month}/${day} ${time}`;
  }
  return "";
}

function isValidDate(date) {
  return date instanceof Date && !isNaN(date);
}
</script>

<style scoped>
.timer-table /deep/ .v-data-table__wrapper {
  max-height: 450px !important;
}
.top-align {
  vertical-align: top;
  padding-top: 14px !important;
}

.right-align {
  text-align: right !important;
}

.border-right {
  border-right: thin solid rgba(0, 0, 0, 0.12);
}
.text-wrap{
  white-space: normal !important;
  word-wrap: break-word !important; 
  word-break: break-word;
  overflow-wrap: anywhere;
}
</style>